import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Spin,
  message,
  Tag,
  Space,
  Button,
  Flex,
  Progress,
  Switch,
} from "antd";
import {} from "@ant-design/icons";
import {
  ArrowLeftOutlined,
  TrophyOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import axios from "axios";

import { useNavigate, useParams } from "react-router-dom";

import feedback_selected from "../img/strategies_icons/rc/rc_think_aloud.png";
import record_selected from "../img/strategies_icons/rf/rf_record_listen.png";
import authoring_selected from "../img/strategies_icons/rc/rc_authoring_effects.png";


import read_aloud_read_theatre from "../img/strategies_icons/rf/rf_performance.png";
import read_aloud_model_selected from "../img/strategies_icons/rf/rf_modelled_reading.png";


import word_clarification_selected from "../img/strategies_icons/ws/ws_contextual_analysis.png";
import word_wall_selected from "../img/strategies_icons/ws/ws_word_cloud.png";

import reflection_selected from "../img/strategies_icons/reflection.png";

import { taskRegistry } from "../config/taskRegistry";
import { reflectionForms } from "../config/reflectionForms";

const { Title, Text, Paragraph } = Typography;

const vorlesenIcons = {
  read_theatre_probe_final: read_aloud_read_theatre,
  listen_understand: read_aloud_model_selected,
};

const taskIcons = {
  feedback: feedback_selected,
  record: record_selected,
  enrichments: authoring_selected,
  vorlesen: read_aloud_model_selected,
  hexiLexi: word_clarification_selected,
  analyze: word_wall_selected,
  reflection_1: reflection_selected,
  reflection_2: reflection_selected,
  reflection_3: reflection_selected,
  reflection_4: reflection_selected,
  reflection_5: reflection_selected,
  reflection_6: reflection_selected,
};

const Tasks = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);

  const [activity, setActivity] = useState(null);

  const [storyTitle, setStoryTitle] = useState("");
  const [storyImg, setStoryImg] = useState(null);

  const completedCount = tasks.filter((t) => t.status === "completed").length;
  const totalCount = tasks.length;
  const progressPercent =
    totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

  const [showConfetti, setShowConfetti] = useState(false);

  const pathRef = useRef(null);
  const cardRefs = useRef([]);

  const [points, setPoints] = useState([]);

  const [totalLength, setTotalLength] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newPoints = cardRefs.current.map((el) => {
        if (!el) return null;

        const rect = el.getBoundingClientRect();
        const parentRect = el.parentElement.getBoundingClientRect();

        return {
          x: rect.left - parentRect.left + rect.width / 2,
          y: rect.top - parentRect.top + rect.height / 2,
        };
      });

      setPoints(newPoints);
    }, 50); // small delay ensures layout is done

    return () => clearTimeout(timer);
  }, [tasks]);

  useEffect(() => {
    if (pathRef.current) {
      setTotalLength(pathRef.current.getTotalLength());
    }
  }, [tasks]);

  const fetchData = async () => {
    try {
      setLoading(true);


       // Fetch the activity list and find the activity selected by its URL ID.
    const activitiesRes = await axios.get("/api/activities/user");

    const selectedActivity = activitiesRes.data.find(
      (item) => String(item.id) === String(id)
    );

    if (!selectedActivity) {
      message.error("Aktivität nicht gefunden.");
      navigate("/activities", { replace: true });
      return;
    }

    setActivity(selectedActivity);

      /* ---------- FETCH TASKS ---------- */
      const tasksRes = await axios.get(`/api/activities/${id}/tasks`);

      //const sorted = tasksRes.data.sort((a, b) => b.task_id - a.task_id);

      const sorted = tasksRes.data.sort((a, b) => {
        return (a.seq_no ?? 999) - (b.seq_no ?? 999);
      });

      setTasks(sorted);

      if (sorted.length > 0) {
        const firstTask = sorted[0];

        const storyRes = await axios.get("/api/stories/");
        const story = storyRes.data.find(
          (s) => s.title === firstTask.story_title
        );

        if (story) {
          setStoryTitle(story.title);
          setStoryImg(story.img); // or story.image depending on API
        } else {
          console.warn("Story not found for title:", firstTask.story_title);
          setStoryTitle(firstTask.story_title);
          setStoryImg(null);
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Laden der Aufgaben.");
    } finally {
      setLoading(false);
    }
  };

  const getDescriptionText = (taskKey, descriptionId) => {
    // Normal tasks
    const task = taskRegistry?.[taskKey];
    if (task && task.descriptions) {
      const desc = task.descriptions.find((d) => d.id === descriptionId);
      if (desc) return desc.text;
    }

    // Reflection tasks
    const reflection = reflectionForms?.[taskKey];
    if (reflection) {
      return reflection.description;
    }

    return descriptionId || "Keine Beschreibung";
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return <Spin size="large" className="loader" />;
  }

  if (!activity) {
    return null;
  }

  const handleToggleTaskCompletion = async (taskId, currentStatus) => {
    const newStatus = currentStatus === "completed" ? "pending" : "completed";

    try {
      await axios.put(`/api/tasks/${taskId}/status`, { status: newStatus });

      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.task_id === taskId ? { ...task, status: newStatus } : task
        )
      );

      // Calculate progress after this toggle
      const updatedCompletedCount =
        tasks.filter((t) => t.status === "completed").length +
        (newStatus === "completed" ? 1 : -1);
      const totalCount = tasks.length;

      if (updatedCompletedCount === totalCount && totalCount > 0) {
        // Show confetti only when the user completes the last task
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      message.error("Fehler beim Aktualisieren der Aufgabe");
    }
  };

  let firstIncompleteIndex = tasks.findIndex((t) => t.status !== "completed");

  if (firstIncompleteIndex === -1) {
    firstIncompleteIndex = tasks.length; // 👈 unlock everything
  }

  const pathD = points
    .map((p, i) => {
      if (!p) return "";

      if (i === 0) return `M ${p.x} ${p.y + 250}`;

      const prev = points[i - 1];
      if (!prev) return "";

      const c1x = prev.x + (p.x - prev.x) / 2;
      const c1y = prev.y;

      const c2x = prev.x + (p.x - prev.x) / 2;
      const c2y = p.y;

      return `C ${c1x} ${c1y + 250}, ${c2x} ${c2y + 250}, ${p.x} ${p.y + 250}`;
    })
    .join(" ");

  const headerState =
    progressPercent === 100
      ? "completed"
      : progressPercent > 0
      ? "in-progress"
      : "not-started";


      const lastCompletedIndex = tasks
      .map((t, i) => (t.status === "completed" ? i : -1))
      .filter((i) => i !== -1)
      .pop();

  return (
    <div style={{ padding: "20px" }}>
      {/* BACK BUTTON */}
      <Button
        icon={<ArrowLeftOutlined />}
        size="large"
        onClick={() => navigate("/activities")}
        style={{ marginBottom: 20 }}
      >
        Zurück zu Aktivitäten
      </Button>

      {/* HEADER */}
      <div className="task-header-wrapper">
        <Card
          className={`task-header-card ${headerState}`}
          cover={
            <img
              src={`/api/stories/imagefile/${storyImg}`}
              alt={storyTitle}
              className="task-image"
            />
          }
        >
          <div style={{ flexGrow: 1, paddingTop: 8 }}>
            <Title level={2} className="task-title">
              {activity.title}
            </Title>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 8,
              }}
            >
              <Text className="story-label">
                <ReadOutlined style={{ fontSize: 24 }} />
                Geschichte: {storyTitle}
              </Text>
            </div>

            <div className="progress-wrapper">
              <Progress
                percent={progressPercent}
                strokeWidth={28}
                showInfo
                percentPosition={{ align: "center", type: "inner" }}
                status={progressPercent === 100 ? "success" : "active"}
                className={`task-progress is-big ${
                  progressPercent === 100
                    ? "is-complete"
                    : progressPercent > 0
                    ? "is-active"
                    : "is-not-started"
                }`}
              />

              <TrophyOutlined className={`trophy-icon ${headerState}`} />
            </div>

            {showConfetti && <div className="confetti">🎉✨🏆✨🎉</div>}
          </div>
        </Card>
      </div>

      {/* TASK GRID */}
      <div
        style={{
          padding: "10px",
          margin: "0",
          overflow: "auto",
          borderRadius: 22,
          boxShadow: "0 4px 10px rgba(250, 1, 1, 0.05)",
        }}
      >
        {/* STORY TITLE */}

        <div className="separator2">Deine Aufgaben</div>

        {/* HORIZONTAL PATH */}
        <div
          style={{
            position: "relative",
            overflowX: "auto",
            padding: "0px 0px 80px 0px", // extra left padding fixes first card cut
          }}
        >
          {points.length > 0 && (
            <svg
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: tasks.length * 450,
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              {/* BACKGROUND PATH */}
              <path
                ref={pathRef}
                d={pathD}
                stroke="#eee"
                strokeWidth="30"
                fill="none"
                strokeLinecap="round"
              />

              {/* PROGRESS PATH */}
              {totalLength > 0 && (
                <path
                  d={pathD}
                  stroke="#a9c46a"
                  strokeWidth="30"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={totalLength}
                  strokeDashoffset={
                    progressPercent === 0
                      ? totalLength
                      : totalLength * (1 - progressPercent / 100)
                  }
                  style={{
                    transition: "stroke-dashoffset 0.6s ease",
                  }}
                />
              )}

              {/* FLOW LINE */}
              <path
                d={pathD}
                stroke="#bbd08a"
                strokeWidth="6"
                fill="none"
                strokeDasharray="10 10"
                style={{
                  animation: "flow 2s linear infinite",
                }}
              />

              {points.map((p, i) => {
                if (!p) return null;

                const isNext = i === firstIncompleteIndex;
                const task = tasks[i];

                return (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y + 250}
                    r={18}
                    fill={
                      task.status === "completed"
                        ? "#7b973d"
                        : isNext
                        ? "#7960b0"
                        : "#bdb0d8"
                    }
                    stroke="#fff"
                    strokeWidth="4"
                    style={{
                      transformBox: "fill-box",
                      animation: isNext ? "pulse 1.5s infinite" : "none",
                      filter: isNext ? "drop-shadow(0 0 10px #7d54ef)" : "none",
                    }}
                  />
                );
              })}
            </svg>
          )}

          <div
            style={{
              display: "flex",
              gap: 80,
              position: "relative",
              minWidth: tasks.length * 450, // forces container to grow
            }}
          >
            {tasks.map((task, index) => {
              const isLocked = index > firstIncompleteIndex;
              const isReflection = task.category?.startsWith("reflection");

              const route = isReflection
                ? `/reflection/${task.task_id}/${task.category}`
                : "/StoryReader";

              const isSingle = tasks.length === 1;
              let icon = taskIcons[task.category];

if (task.category === "vorlesen") {
  icon = vorlesenIcons[task.description] || icon;
}

              const taskDef = taskRegistry?.[task.category];
              const descriptionDef = taskDef?.descriptions?.find(
                (d) => d.id === task.description
              );

              const displayLabel =
                descriptionDef?.label ||
                taskDef?.label ||
                reflectionForms?.[task.category]?.label ||
                task.category;

              console.log("task.category::::", task.category);
              console.log("icon::::", icon);
              return (
                <div
                  ref={(el) => (cardRefs.current[index] = el)}
                  key={task.task_id}
                  style={{
                    minWidth: 300,
                    marginTop: index % 2 === 0 ? 0 : 30,
                  }}
                >
                  <Card className="task-card" hoverable>
                    <div className="task-step-badge">Aufgabe {task.seq_no}</div>
                    <div className="task-card-content">
                      <div>
                        {icon && (
                          <img src={icon} alt="" className="task-icon" />
                        )}

                        {/* <Text type="secondary">
                          Rolle: {task.character_name || "Alle"}
                        </Text> */}

                        <Text strong className="task-category">
                          {displayLabel}
                        </Text>

                        <Paragraph
                          ellipsis={{
                            rows: 2,
                            expandable: true,
                            symbol: "mehr anzeigen",
                          }}
                          className="task-description"
                        >
                          {task.description
                            ? getDescriptionText(
                                task.category,
                                task.description
                              )
                            : "Keine Beschreibung"}
                        </Paragraph>
                      </div>
                    </div>

                    <Button
                      type="primary"
                      size="large"
                      block
                      disabled={isLocked}
                      className="task-action-btn"
                      onClick={() =>
                        navigate(route, {
                          state: {
                            storyId: task.story_id,
                            groupId: task.group_id,
                            taskCategory: task.category,
                            title: storyTitle,
                            img: `/api/stories/imagefile/${storyImg}`,
                            activity,
                          },
                        })
                      }
                    >
                      {isLocked
                        ? "🔒 Erst vorherige Aufgabe lösen"
                        : "Mach die Aufgabe"}
                    </Button>

                    <div className="task-status-wrapper">
                      <Space align="center">
                        <Text strong>Bist du fertig?</Text>

                        <Switch
                          checked={task.status === "completed"}
                          disabled={
                            task.status === "completed" && index !== lastCompletedIndex
                          }
                          onChange={(checked) =>
                            handleToggleTaskCompletion(
                              task.task_id,
                              checked ? "pending" : "completed"
                            )
                          }
                          checkedChildren="Ja"
                          unCheckedChildren="Nein"
                      />
                      </Space>
                    </div>

                    {isLocked && <div className="task-locked-overlay">🔒</div>}
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
